// Pages publiques à jeton : réponse de l'employeur (F-024) et engagement du garant (F-032).
import { useState } from "react";
import { useParams } from "react-router";
import { post } from "@core/api";
import { useGet } from "@core/hooks";
import { dateTime, money } from "@core/format";
import { PublicShell } from "@core/layout";
import { Button, Checkbox, ErrorBox, Input, Loading, Notice, Textarea, fieldError } from "@core/ui";
import { ChoiceGroup } from "./shared";

type EmployerView = { state: "ouverte" | "repondue" | "expiree" | "annulee"; candidate_name?: string; org_name?: string; expires_at?: string; contract_types?: { value: string; label: string }[] };

const TRI = [
  { value: "oui", label: "Oui" },
  { value: "non", label: "Non" },
  { value: "ne_sait_pas", label: "Je ne sais pas" },
];

export function EmployerConfirmationPage() {
  const { token = "" } = useParams();
  const q = useGet<EmployerView>(`/api/v1/public/employer-confirmations/${encodeURIComponent(token)}`);
  const [form, setForm] = useState({ respondent_name: "", respondent_role: "", respondent_email: "", acts_for_employer: false, declines: false, in_post: "", contract_type: "", trial_period_over: "", notice_in_progress: "" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  if (q.isLoading) return <PublicShell><Loading /></PublicShell>;
  if (q.error) {
    return (
      <PublicShell>
        <h1>Lien introuvable</h1>
        <p>Ce lien de confirmation n’existe pas ou n’est plus valable.</p>
      </PublicShell>
    );
  }
  const v = q.data!;
  if (done) {
    return (
      <PublicShell>
        <h1>Merci</h1>
        <Notice tone="succes">{done}</Notice>
      </PublicShell>
    );
  }
  if (v.state !== "ouverte") {
    const text = v.state === "repondue" ? "Une réponse a déjà été transmise pour cette demande. Merci." : v.state === "expiree" ? "Ce lien a expiré. Le bailleur peut vous en envoyer un nouveau si nécessaire." : "Cette demande a été annulée : aucune réponse n’est attendue.";
    return (
      <PublicShell>
        <h1>Confirmation d’emploi</h1>
        <p>{text}</p>
      </PublicShell>
    );
  }
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = form.declines
        ? { respondent_name: form.respondent_name, respondent_role: form.respondent_role, respondent_email: form.respondent_email || null, acts_for_employer: form.acts_for_employer, declines: true }
        : { ...form, respondent_email: form.respondent_email || null };
      const r = await post<{ message: string }>(`/api/v1/public/employer-confirmations/${encodeURIComponent(token)}`, body);
      setDone(r.message);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <PublicShell>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <h1>Confirmation d’emploi</h1>
        <p>
          {v.org_name}, bailleur, vous demande de confirmer quelques informations sur l’emploi de <strong>{v.candidate_name}</strong>, avec son accord. Seules les quatre questions ci-dessous sont posées. Lien personnel, valable une seule fois jusqu’au {dateTime(v.expires_at)}.
        </p>
        <Notice>Vous pouvez indiquer que vous ne souhaitez pas répondre. Si vous n’êtes pas la bonne personne, fermez simplement cette page.</Notice>
        <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
        <div className="form-row">
          <Input label="Votre nom" required value={form.respondent_name} error={fieldError(error, "respondent_name")} onChange={(e) => setForm({ ...form, respondent_name: e.target.value })} />
          <Input label="Votre fonction" required placeholder="Ex. responsable RH" value={form.respondent_role} error={fieldError(error, "respondent_role")} onChange={(e) => setForm({ ...form, respondent_role: e.target.value })} />
          <Input label="Votre adresse e-mail professionnelle (facultatif)" type="email" value={form.respondent_email} onChange={(e) => setForm({ ...form, respondent_email: e.target.value })} />
        </div>
        <Checkbox label="Je réponds pour le compte de l’employeur de cette personne." checked={form.acts_for_employer} onChange={(c) => setForm({ ...form, acts_for_employer: c })} />
        {fieldError(error, "acts_for_employer") ? (
          <p className="bv-champ-aide decision-erreur" role="alert">
            {fieldError(error, "acts_for_employer")}
          </p>
        ) : null}
        <Checkbox label="Je ne souhaite pas répondre à cette demande." checked={form.declines} onChange={(c) => setForm({ ...form, declines: c })} />
        {!form.declines ? (
          <>
            <ChoiceGroup legend="1. La personne est-elle en poste à ce jour ?" name="en-poste" value={form.in_post} onChange={(x) => setForm({ ...form, in_post: x })} options={TRI} />
            {fieldError(error, "in_post") ? <p className="bv-champ-aide decision-erreur">{fieldError(error, "in_post")}</p> : null}
            <ChoiceGroup legend="2. Type de contrat" name="contrat" value={form.contract_type} onChange={(x) => setForm({ ...form, contract_type: x })} options={v.contract_types ?? []} />
            {fieldError(error, "contract_type") ? <p className="bv-champ-aide decision-erreur">{fieldError(error, "contract_type")}</p> : null}
            <ChoiceGroup legend="3. La période d’essai est-elle terminée ?" name="essai" value={form.trial_period_over} onChange={(x) => setForm({ ...form, trial_period_over: x })} options={[...TRI, { value: "sans_objet", label: "Pas de période d’essai" }]} />
            {fieldError(error, "trial_period_over") ? <p className="bv-champ-aide decision-erreur">{fieldError(error, "trial_period_over")}</p> : null}
            <ChoiceGroup legend="4. Un préavis (démission, licenciement, fin de contrat) est-il en cours ?" name="preavis" value={form.notice_in_progress} onChange={(x) => setForm({ ...form, notice_in_progress: x })} options={TRI} />
            {fieldError(error, "notice_in_progress") ? <p className="bv-champ-aide decision-erreur">{fieldError(error, "notice_in_progress")}</p> : null}
          </>
        ) : null}
        <p className="small muted">Votre réponse est datée et transmise au bailleur avec votre nom et votre fonction déclarés. Aucune autre information sur la personne ne vous est demandée.</p>
        <div className="form-actions">
          <Button type="submit" busy={busy}>
            {form.declines ? "Indiquer que je ne réponds pas" : "Envoyer ma réponse"}
          </Button>
        </div>
      </form>
    </PublicShell>
  );
}

type GuarantorView = {
  state: "ouverte" | "expiree" | "close";
  guarantor_name?: string;
  tenants?: string[];
  property_label?: string;
  landlord?: string;
  rent_cents?: number;
  charges_cents?: number;
  amount_cents?: number;
  amount_words?: string;
  duration_months?: number | null;
  engagement_type?: string;
  mention?: string;
  expires_at?: string;
};

export function GuarantorCommitmentPage() {
  const { token = "" } = useParams();
  const url = `/api/v1/public/guarantor-commitments/${encodeURIComponent(token)}`;
  const q = useGet<GuarantorView>(url);
  const [form, setForm] = useState({ full_name: "", mention: "", accept: false });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  if (q.isLoading) return <PublicShell><Loading /></PublicShell>;
  if (q.error || !q.data || q.data.state !== "ouverte") {
    return (
      <PublicShell>
        <h1>Engagement de caution</h1>
        <p>{q.data?.state === "expiree" ? "Cette invitation a expiré. Demandez au bailleur une nouvelle invitation." : "Ce lien n’est plus valable ou l’engagement a déjà été confirmé."}</p>
      </PublicShell>
    );
  }
  const v = q.data;
  if (done) {
    return (
      <PublicShell>
        <h1>Merci</h1>
        <Notice tone="succes">{done}</Notice>
      </PublicShell>
    );
  }
  return (
    <PublicShell>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            const r = await post<{ message: string }>(url, form);
            setDone(r.message);
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <h1>Votre engagement de caution</h1>
        <p>
          Bonjour {v.guarantor_name}. Vous êtes indiqué comme garant de {v.tenants?.join(" et ")} pour « {v.property_label} » (bailleur : {v.landlord}). Loyer {money(v.rent_cents)} et charges {money(v.charges_cents)} par mois.
        </p>
        <Notice tone="attention" title="Avant de vous engager">
          Si le locataire ne paie pas, vous devrez payer à sa place, dans la limite de {money(v.amount_cents)} ({v.amount_words}), {v.duration_months ? `pendant ${v.duration_months} mois` : "pour une durée indéterminée"}
          {v.engagement_type === "solidaire" ? ", sans pouvoir exiger que le bailleur poursuive d’abord le locataire" : ""}. Personne ne peut confirmer à votre place.
        </Notice>
        <p>
          <a href={`${url}/acte`} target="_blank" rel="noreferrer">
            Lire l’acte de cautionnement complet (PDF)
          </a>
        </p>
        <blockquote className="decision-mention">{v.mention}</blockquote>
        <Input label="Vos nom et prénom" required value={form.full_name} error={fieldError(error, "full_name")} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
        <Textarea label="Recopiez vous-même la mention ci-dessus" required value={form.mention} error={fieldError(error, "mention")} onChange={(e) => setForm({ ...form, mention: e.target.value })} />
        <Checkbox label="J’ai lu l’acte et je m’engage en qualité de caution dans ces conditions." checked={form.accept} onChange={(c) => setForm({ ...form, accept: c })} />
        {fieldError(error, "accept") ? (
          <p className="bv-champ-aide decision-erreur" role="alert">
            {fieldError(error, "accept")}
          </p>
        ) : null}
        <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
        <p className="small muted">Cette confirmation est enregistrée et datée. L’acte vous sera ensuite proposé à la signature. Lien valable jusqu’au {dateTime(v.expires_at)}.</p>
        <div className="form-actions">
          <Button type="submit" busy={busy}>
            Confirmer mon engagement
          </Button>
        </div>
      </form>
    </PublicShell>
  );
}
